import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import type { AdornisUser } from '@adornis/users/db/a-user.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { User } from '@dla/db/User.js';

export enum PermissionType {
  EVERYBODY = 'everybody',
  RESTRICTED = 'restricted',
}

export const GUEST_USER_ID = 'guest';
export const ANYONE_USER_ID = 'anyone';

@Entity()
export class DocumentPermission extends AdornisEntity {
  static override _class = 'DocumentPermission';

  static override resolveID(entity: any): string {
    return entity.userID;
  }

  @DataLoadedField(type => User, ({ userID }) => userID)
  user: Maybe<User>;

  @Field(type => String)
  userID: Maybe<string>;

  @Field(type => Boolean)
  canRead: Maybe<boolean>;

  @Field(type => Boolean)
  canEdit: Maybe<boolean>;

  get name() {
    if (this.userID === GUEST_USER_ID) {
      return 'Guest';
    }

    if (this.userID === ANYONE_USER_ID) {
      return 'Any Logged In';
    }

    return this.user?.username ?? 'Unknown';
  }
}

@Entity()
export class DocumentMeta extends MongoEntity {
  static override _class = 'DocumentMeta';
  static override _collectionName = 'document-meta';

  @Field(type => String)
  title: Maybe<string>;

  @Field(type => String)
  createdBy: Maybe<string>;

  @Field(type => String)
  permissionType: Maybe<PermissionType>;

  @Field(type => [DocumentPermission])
  permissions: Maybe<DocumentPermission[]>;

  get ensurePermissions() {
    if (!this.permissions?.find(perm => perm.userID === GUEST_USER_ID)) {
      if (!this.permissions) {
        this.permissions = [];
      }
      this.permissions?.push(
        new DocumentPermission({
          userID: GUEST_USER_ID,
          canRead: false,
          canEdit: false,
        }),
      );
    }
    if (!this.permissions?.find(perm => perm.userID === ANYONE_USER_ID)) {
      if (!this.permissions) {
        this.permissions = [];
      }
      this.permissions?.push(
        new DocumentPermission({
          userID: ANYONE_USER_ID,
          canRead: false,
          canEdit: false,
        }),
      );
    }
    return this.permissions ?? [];
  }

  /**
   * Fetches the permission for the guest user.
   * @returns The permission for the guest user.
   */
  guestPermission() {
    return (
      this.permissions?.find(perm => perm.userID === GUEST_USER_ID) ??
      new DocumentPermission({
        userID: GUEST_USER_ID,
        canRead: false,
        canEdit: false,
      })
    );
  }

  /**
   * Fetches the permission for the anyone user.
   * @returns The permission for the anyone user.
   */
  anyonePermission() {
    return (
      this.permissions?.find(perm => perm.userID === ANYONE_USER_ID) ??
      new DocumentPermission({
        userID: ANYONE_USER_ID,
        canRead: false,
        canEdit: false,
      })
    );
  }

  /**
   * Checks if the user is the owner of the document. If no user is provided, the current user is used.
   * @param user The user to check.
   * @returns Whether the user is the owner of the document.
   */
  isOwner(user?: Maybe<User>) {
    user ??= currentUser().optimisticValue?.data;
    return this.createdBy === user?._id;
  }

  /**
   * Checks if the user can read the document. If no user is provided, the current user is used.
   * @param user The user to check.
   * @returns Whether the user can read the document.
   */
  canRead(user?: Maybe<User>) {
    if (this.permissionType === PermissionType.EVERYBODY) {
      return true;
    }

    user ??= currentUser().optimisticValue?.data;
    if (!user) {
      return this.guestPermission().canRead;
    }

    if (this.createdBy === user._id) {
      return true;
    }

    return this.permissions?.find(perm => perm.userID === user?._id)?.canRead ?? this.anyonePermission().canRead;
  }

  /**
   * Checks if the user can edit the document. If no user is provided, the current user is used.
   * @param user The user to check.
   * @returns Whether the user can edit the document.
   */
  canEdit(user?: Maybe<User>) {
    if (this.permissionType === PermissionType.EVERYBODY) {
      return true;
    }

    user ??= currentUser().optimisticValue?.data;
    if (!user) {
      return this.guestPermission().canEdit;
    }

    if (this.createdBy === user._id) {
      return true;
    }

    return this.permissions?.find(perm => perm.userID === user?._id)?.canEdit ?? this.anyonePermission().canEdit;
  }
}

/**
 * Ensures that the document meta exists for the given document ID. If the document meta does not exist, it will be created with default values.
 * @param rid The ID of the document.
 */
export const ensureDocumentMeta = registerMutation({
  type: () => Boolean,
  operationName: 'ensureDocumentMeta',
  resolve: (rid: string, moodleMode: boolean = false) => {
    return async () => {
      const caller = await CurrentUserInfo.getMyself<AdornisUser>()({
        _id: 1,
      });

      const docMeta = await DocumentMeta.getByID<DocumentMeta>(rid)({
        _id: 1,
        permissions: {
          userID: 1,
        },
      });

      try {
        if (!docMeta) {
          if (!moodleMode) {
            await new DocumentMeta({
              _id: rid,
              title: 'Untitled',
              createdBy: caller?._id,
              permissionType: PermissionType.EVERYBODY,
              permissions: [],
            }).create();
          } else {
            await new DocumentMeta({
              _id: rid,
              title: 'Untitled',
              createdBy: caller?._id,
              permissionType: PermissionType.RESTRICTED,
              permissions: [
                new DocumentPermission({
                  userID: GUEST_USER_ID,
                  canRead: false,
                  canEdit: false,
                }),
                new DocumentPermission({
                  userID: ANYONE_USER_ID,
                  canRead: true,
                  canEdit: true,
                }),
              ],
            }).create();
          }
        } else {
          if (!docMeta.permissions?.find(perm => perm.userID === GUEST_USER_ID)) {
            if (!docMeta.permissions) {
              docMeta.permissions = [];
            }
            docMeta.permissions?.push(
              new DocumentPermission({
                userID: GUEST_USER_ID,
                canRead: false,
                canEdit: false,
              }),
            );
            await docMeta.save();
          }
        }
      } catch (e: any) {
        // * This can happen if the user that ensures doesn't have permission to edit the document
        // * Failing is ok, we just log the error.
        logger.warn({ id: rid }, 'error while ensuring document meta');
      }

      return true;
    };
  },
  options: {},
  params: [
    { name: 'rid', type: () => String },
    { name: 'moodleMode', type: () => Boolean },
  ],
});
